import { useState } from 'react';
import { type CountryCode, type AuthMethod, type AuthStep, type LookupFailedInfo, type AccessBlockedInfo, COUNTRY_CODES } from '../types';

// Re-export types for backward compatibility
export type { AuthMethod, AuthStep };

/**
 * Custom hook to manage authentication UI state
 *
 * Manages:
 * - Authentication method selection (phone vs email vs username)
 * - Current step in auth flow (input vs verify vs forgot-password vs reset-password)
 * - Country code selection for phone auth
 * - Phone number state (local and international format)
 * - Email address state
 * - Username/password state for username auth
 * - Verification code state
 * - Error messages
 * - User details capture flow
 *
 * @returns Authentication state and updater functions
 */
export const useAuthState = () => {
  // Auth method and step
  const [authMethod, setAuthMethod] = useState<AuthMethod>('phone');
  const [authStep, setAuthStep] = useState<AuthStep>('input');

  // Phone auth state
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(COUNTRY_CODES[0]); // Default to Australia
  const [phoneNumber, setPhoneNumber] = useState('');
  const [localPhoneNumber, setLocalPhoneNumber] = useState(''); // Phone number without country code

  // Email auth state
  const [email, setEmail] = useState('');

  // Username/password auth state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSignup, setIsSignup] = useState(false); // Toggle between login and signup mode

  // Verification state
  const [code, setCode] = useState('');

  // Password reset state
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [resetIdentifierType, setResetIdentifierType] = useState<'email' | 'phone'>('email');

  // UI state
  const [error, setError] = useState('');
  const [showCaptureDetails, setShowCaptureDetails] = useState(false);
  const [verifiedUserId, setVerifiedUserId] = useState<string | null>(null);
  const [verifiedUserData, setVerifiedUserData] = useState<any>(null);
  const [pendingToken, setPendingToken] = useState<string | null>(null);

  // Set when verifySmsCode/verifyEmailCode returns accountStatus=LOOKUP_FAILED — drives the NoAccountFoundCard view.
  const [lookupFailedInfo, setLookupFailedInfo] = useState<LookupFailedInfo | null>(null);
  // Set when a login path answers ACCESS_BLOCKED — drives the AccessBlockedCard view.
  const [accessBlockedInfo, setAccessBlockedInfo] = useState<AccessBlockedInfo | null>(null);

  const resetState = () => {
    setAuthMethod('phone');
    setAuthStep('input');
    setSelectedCountry(COUNTRY_CODES[0]);
    setPhoneNumber('');
    setLocalPhoneNumber('');
    setEmail('');
    setUsername('');
    setPassword('');
    setConfirmPassword('');
    setIsSignup(false);
    setCode('');
    setResetToken(null);
    setError('');
    setLookupFailedInfo(null);
    setAccessBlockedInfo(null);
  };

  const switchToEmail = () => {
    setAuthMethod('email');
    setAuthStep('input');
    setError('');
  };

  const switchToPhone = () => {
    setAuthMethod('phone');
    setAuthStep('input');
    setError('');
  };

  const switchToUsername = () => {
    setAuthMethod('username');
    setAuthStep('input');
    setIsSignup(false);
    setError('');
  };

  const goToForgotPassword = () => {
    setAuthStep('forgot-password');
    setError('');
    setCode('');
    setResetToken(null);
  };

  const goBackToLogin = () => {
    setAuthStep('input');
    setError('');
    setCode('');
    setResetToken(null);
    setPassword('');
    setConfirmPassword('');
  };

  return {
    // State
    authMethod,
    authStep,
    selectedCountry,
    phoneNumber,
    localPhoneNumber,
    email,
    username,
    password,
    confirmPassword,
    isSignup,
    code,
    resetToken,
    resetIdentifierType,
    error,
    showCaptureDetails,
    verifiedUserId,
    verifiedUserData,
    pendingToken,
    lookupFailedInfo,
    accessBlockedInfo,

    // Setters
    setAuthMethod,
    setAuthStep,
    setSelectedCountry,
    setPhoneNumber,
    setLocalPhoneNumber,
    setEmail,
    setUsername,
    setPassword,
    setConfirmPassword,
    setIsSignup,
    setCode,
    setResetToken,
    setResetIdentifierType,
    setError,
    setShowCaptureDetails,
    setVerifiedUserId,
    setVerifiedUserData,
    setPendingToken,
    setLookupFailedInfo,
    setAccessBlockedInfo,

    // Actions
    resetState,
    switchToEmail,
    switchToPhone,
    switchToUsername,
    goToForgotPassword,
    goBackToLogin,
  };
};
